/* eslint-disable react/prop-types */
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  Button,
  makeStyles,
  DialogActions,
  DialogContent,
  CircularProgress,
  Snackbar,
  TextField
} from "@material-ui/core";
import MuiAlert from "@material-ui/lab/Alert";
import { deleteAthenaTenant } from "lib/services/athenaTenants";

const useStyles = makeStyles(theme => ({
  addUserButton: {
    backgroundColor: "#90cb7c"
  },
  button: {
    margin: theme.spacing(1)
  },
  wrapper: {
    margin: theme.spacing(1),
    position: "relative"
  },
  buttonProgress: {
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12
  },
  inputLabelRoot: {
    clip: "rect(0 0 0 0)",
    clipPath: "inset(50%)",
    height: 1,
    overflow: "hidden",
    position: "absolute",
    whiteSpace: "nowrap",
    width: 1
  },
  inputFormControl: {
    "label + &": {
      marginTop: 0
    }
  }
}));

function Alert(props) {
  // eslint-disable-next-line react/jsx-props-no-spreading
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

export default function DeleteAthenaTenantComponent({
  athenaTenant,
  open,
  onSuccess,
  onDismiss
}) {
  const classes = useStyles(() => ({
    dialogContent: {
      padding: "20px"
    }
  }));
  const [loading, setLoading] = useState(false);
  const [btnDisabled, setBtnDisabled] = useState(true);
  const [snackbarState, setSnackbarState] = useState({
    open: false,
    severity: "",
    message: ""
  });

  const handleDeleteConfirm = async e => {
    e.preventDefault();
    setLoading(true);
    const newSnackbarState = {
      open: true,
      severity: "success",
      message: `${athenaTenant.company.name} tenant deleted successfully!`
    };

    try {
      await deleteAthenaTenant(athenaTenant.id);
      onSuccess();
    } catch (error) {
      if (
        error.response &&
        error.response.data.detail ===
          "Cannot delete athena tenant: one or more users are assigned to this tenant"
      ) {
        newSnackbarState.severity = "error";
        newSnackbarState.message =
          "Tenant can't be deleted because one or more users are assigned to it.";
      } else {
        newSnackbarState.severity = "error";
        newSnackbarState.message =
          "An error occurred. Please try again shortly";
      }
    } finally {
      setLoading(false);
      setSnackbarState(newSnackbarState);
    }
  };

  const handleSnackbarClose = (_event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setSnackbarState(prevState => {
      return {
        ...prevState,
        open: false
      };
    });
  };

  useEffect(() => {
    setBtnDisabled(true);
  }, [onDismiss]);

  return (
    <>
      <Dialog
        onClose={onDismiss}
        className={classes.dialogContent}
        open={open}
        aria-labelledby="delete-tenant-form-dialog-title"
      >
        <DialogTitle id="delete-tenant-form-dialog-title">
          Permanently delete tenant 
          {' '}
          <b>{athenaTenant?.company.name}</b>
          ?
        </DialogTitle>
        <DialogContent>
          This action is not reversible. To confirm deletion, enter
          {" "}
          <b>
            <em>{athenaTenant?.company.short_name}</em>
          </b>
          {" "}
          in the field.
          <TextField
            id="confirmation-tenant-delete"
            InputProps={{
              classes: {
                formControl: classes.inputFormControl
              }
            }}
            InputLabelProps={{
              className: classes.inputLabelRoot
            }}
            hintText="Type"
            placeholder={athenaTenant?.company.short_name}
            hidden
            label="Confirmation"
            onChange={text =>
              setBtnDisabled(
                text.target.value !== athenaTenant?.company.short_name ||
                  loading
              )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onDismiss}>Cancel</Button>
          <div className={classes.wrapper}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleDeleteConfirm}
              disabled={btnDisabled}
            >
              Delete tenant
            </Button>
            {loading && (
              <CircularProgress size={24} className={classes.buttonProgress} />
            )}
          </div>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbarState.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarState.severity}>
          {snackbarState.message}
        </Alert>
      </Snackbar>
    </>
  );
}
